import React from "react";
import { Box, Typography, Card } from "@mui/material";
import { styled } from "@mui/material/styles";
import PlanToggleButton from "@/components/PlanToggleButton";

// Import education icons
import imgCareerPlan from "@/assets/icons/education/4-year-plus-grad.svg";
import imgCareerPlan3 from "@/assets/icons/education/career-technical.svg";
import imgCareerPlan4 from "@/assets/icons/education/some-college.svg";
import imgCareerPlan5 from "@/assets/icons/education/4-year-college.svg";
import imgMilitary from "@/assets/icons/education/military.svg";
import img2Year from "@/assets/icons/education/2-year-college.svg";
import imgHighSchool from "@/assets/icons/education/high-school.svg";
import imgCareerPlanPNG from "@/assets/icons/education/4-year-plus-grad.png";
import imgCareerPlan3PNG from "@/assets/icons/education/career-technical.png";
import imgCareerPlan4PNG from "@/assets/icons/education/some-college.png";
import imgCareerPlan5PNG from "@/assets/icons/education/4-year-college.png";
import imgMilitaryPNG from "@/assets/icons/education/military.png";
import img2YearPNG from "@/assets/icons/education/2-year-college.png";
import imgHighSchoolPNG from "@/assets/icons/education/high-school.png";
import SvgColor from "@/components/svg-color";

// Map education IDs to display data
export const educationDisplayMap = {
  "high-school": {
    title: "High School Only",
    description:
      "Complete your high school degree, then get skills and experience by working.",
    icon: imgHighSchool,
    iconPNG: imgHighSchoolPNG,
    color: "#5119b7",
  },
  "career-technical": {
    title: "Career / Technical School",
    description:
      "Get job-specific training with a certificate of completion at a technical school or online.",
    icon: imgCareerPlan3,
    iconPNG: imgCareerPlan3PNG,
    color: "#7a4100",
  },
  military: {
    title: "Military Training",
    description:
      "Enlist and complete basic training, then get specialized training for a military job.",
    icon: imgMilitary,
    color: "#065e49",
    iconPNG: imgMilitaryPNG,
  },
  // "some-college": {
  //   title: "Some College",
  //   description:
  //     "Enroll in college courses relevant to your Pathway, without completing a degree.",
  //   icon: imgCareerPlan4,
  //   color: "#637381",
  // },
  "2-year-college": {
    title: "2-Year College",
    description:
      "Complete a 2-year degree (usually an Associate's Degree) at a community or technical college.",
    icon: img2Year,
    color: "#006c9c",
    iconPNG: img2YearPNG,
  },
  "4-year-college": {
    title: "4-Year College",
    description:
      "Complete a 4-year degree (usually a Bachelor's Degree) at a college or university.",
    icon: imgCareerPlan5,
    color: "#c34607",
    iconPNG: imgCareerPlan5PNG,
  },
  "4-year-plus-grad": {
    title: "4-Year College + Grad/Prof School",
    description:
      "After completing your Bachelor's Degree, earn an advanced degree (1-8 years).",
    icon: imgCareerPlan,
    color: "#d90d65",
    iconPNG: imgCareerPlanPNG,
  },
};

interface EducationCardProps {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  selected?: boolean;
  onToggle?: (id: string) => void;
  showToggle?: boolean;
  label?: string;
}

const EducationCardContainer = styled(Card)<{
  selected?: boolean;
  showToggle?: boolean;
}>(({ selected, showToggle }) => ({
  backgroundColor: "#ffffff",
  borderRadius: "14px",
  boxShadow:
    "0px 0px 2px 0px rgba(145,158,171,0.2), 0px 12px 24px -4px rgba(145,158,171,0.12)",
  padding: "16px",
  height: showToggle ? "340px" : "312px",
  width: "220px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  label: "education-card-container",
}));

const EducationIcon = styled(Box)(({ theme }) => ({
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  label: "education-icon",
}));

const EducationTitle = styled(Typography)<{ color?: string }>(
  ({ color = "#1c252e" }) => ({
    fontFamily: "Proxima Nova, sans-serif",
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "30px",
    color: color,
    height: "60px",
    display: "flex",
    alignItems: "center",
    label: "education-title",
  }),
);

const EducationDescription = styled(Typography)(({ theme }) => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 400,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#1c252e",
  flex: 1,
  label: "education-description",
}));

export default function EducationCard({
  id,
  title,
  description,
  icon,
  color,
  selected = false,
  onToggle,
  showToggle = true,
  label,
}: EducationCardProps) {
  const handleToggle = () => {
    if (onToggle) {
      onToggle(id);
    }
  };

  return (
    <EducationCardContainer
      selected={selected}
      aria-label={label}
      showToggle={showToggle}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
        aria-label="education-card-header"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            width: "100%",
          }}
          aria-label="education-icon-container"
        >
          <EducationIcon>
            <SvgColor
              color={color}
              src={icon}
              alt={title}
              sx={{ transform: "scale(1.5)" }}
            />
          </EducationIcon>
        </Box>
        <EducationTitle color={color}>{title}</EducationTitle>
      </Box>

      <EducationDescription>{description}</EducationDescription>

      {showToggle && (
        <Box sx={{ marginTop: "auto" }} aria-label="education-card-toggle">
          <PlanToggleButton
            fullWidth
            selected={selected}
            onClick={handleToggle}
          />
        </Box>
      )}
    </EducationCardContainer>
  );
}
